<div class="dept__header">
  <h2 mat-dialog-title class="dept__title">{{ isEditMode ? 'Edit department contact' : 'Add department contact' }}</h2>
  <button type="button" class="dept__close" mat-dialog-close aria-label="Close">
    <mat-icon>close</mat-icon>
  </button>
</div>

<mat-dialog-content>
  <form class="dept__form" [formGroup]="departmentForm">
    <!-- Department-name builder: the three pickers compose the display name, and the office
         also fills the (read-only) Address field further down. -->
    <fieldset class="dept__builder" aria-label="Department name">
      <div class="m-label dept__legend">Department name <span class="dept__req">*</span></div>
      <p class="dept__helper">
        Pick the three parts and the name is built for you — product, function, then office. The office also sets the
        address.
      </p>

      @if (loadingLookups) {
        <div class="dept__status"><mat-spinner diameter="22" /></div>
      } @else if (lookupsFailed) {
        <div class="dept__status">
          <span>{{
            isEditMode ? 'Could not load this contact.' : 'Could not load products, functions and offices.'
          }}</span>
          <button type="button" class="dept__retry" (click)="retryLookups()">Try again</button>
        </div>
      } @else {
        <div class="dept__grid">
          <!-- Custom listbox, not a <select>: a native <option> can't render an icon in any
               browser, and the spec draws one per product. -->
          <div class="dept__field dept__combo dept__combo--product">
            <label class="m-label" for="dept-product">Product</label>
            <button
              type="button"
              id="dept-product"
              class="m-select dept__select-btn"
              role="combobox"
              aria-haspopup="listbox"
              aria-controls="dept-product-list"
              [attr.aria-expanded]="productOpen"
              [attr.aria-activedescendant]="productHighlight >= 0 ? 'dept-product-opt-' + productHighlight : null"
              (click)="toggleProductOpen()"
              (blur)="closeProducts()"
              (keydown)="onProductKeydown($event)">
              <span class="dept__select-value">
                @if (selectedProduct) {
                  <img
                    class="dept__select-icon"
                    [ngSrc]="selectedProduct.name | transportModeIcon"
                    width="18"
                    height="18"
                    alt="" />
                  <span>{{ selectedProduct.name }}</span>
                } @else {
                  <span class="dept__select-placeholder">Select...</span>
                }
              </span>
              <mat-icon class="dept__select-chevron material-symbols-outlined">expand_more</mat-icon>
            </button>
            @if (productOpen) {
              <div
                class="sugg-panel"
                id="dept-product-list"
                role="listbox"
                aria-label="Products"
                (mousedown)="$event.preventDefault()">
                <button
                  type="button"
                  role="option"
                  id="dept-product-opt-0"
                  class="sugg"
                  [class.sugg--active]="0 === productHighlight"
                  [attr.aria-selected]="selectedProduct === null"
                  (click)="selectProduct(null)">
                  <span class="dept__chip-text">Select...</span>
                </button>
                @for (product of products; track product.id; let i = $index) {
                  <button
                    type="button"
                    role="option"
                    [id]="'dept-product-opt-' + (i + 1)"
                    class="sugg"
                    [class.sugg--active]="i + 1 === productHighlight"
                    [attr.aria-selected]="product.id === departmentForm.controls.productId.value"
                    (click)="selectProduct(product)">
                    <span class="dept__opt-icon">
                      <img [ngSrc]="product.name | transportModeIcon" width="16" height="16" alt="" />
                    </span>
                    <span class="dept__chip-text">{{ product.name }}</span>
                  </button>
                }
              </div>
            }
          </div>

          <div class="dept__field">
            <label class="m-label" for="dept-function">Function</label>
            <select id="dept-function" class="m-select" formControlName="functionId">
              <option [ngValue]="null">Select...</option>
              @for (fn of functions; track fn.id) {
                <option [ngValue]="fn.id">{{ fn.name }}</option>
              }
            </select>
          </div>

          <!-- Searchable rather than a <select>: there are too many offices to scroll, and the
               term is matched against the address as well as city/office/country. -->
          <div class="dept__field dept__combo">
            <label class="m-label" for="dept-office">Office location <span class="dept__req">*</span></label>
            <input
              id="dept-office"
              class="m-input"
              type="text"
              role="combobox"
              autocomplete="off"
              aria-autocomplete="list"
              aria-controls="dept-office-list"
              [attr.aria-expanded]="officeOpen"
              [attr.aria-activedescendant]="officeHighlight >= 0 ? 'dept-office-opt-' + officeHighlight : null"
              placeholder="Search city, office or address..."
              [formControl]="officeSearch"
              (focus)="openOffices($event)"
              (blur)="closeOffices()"
              (keydown)="onOfficeKeydown($event)" />
            <!-- mousedown is swallowed panel-wide so that clicking anywhere in it — an option or
                 a country header — doesn't blur the input and tear the panel down before the
                 click lands. -->
            @if (officeOpen) {
              <div
                class="sugg-panel"
                id="dept-office-list"
                role="listbox"
                aria-label="Office locations"
                (mousedown)="$event.preventDefault()">
                @for (option of officeOptions; track option.office.id; let i = $index) {
                  @if (option.countryHeader) {
                    <div class="sugg-group" aria-hidden="true">{{ option.countryHeader }}</div>
                  }
                  <button
                    type="button"
                    role="option"
                    [id]="'dept-office-opt-' + i"
                    class="sugg"
                    [class.sugg--active]="i === officeHighlight"
                    [attr.aria-selected]="option.office.id === departmentForm.controls.branchOfficeId.value"
                    [attr.aria-label]="officeLabel(option.office)"
                    (click)="selectOffice(option.office)">
                    <span class="dept__chip-text">
                      <span class="dept__opt-city">{{ option.office.city }}</span>
                      <!-- Only while searching: browsing then reads as a plain list of cities,
                           exactly like the native picker. -->
                      @if (officeFiltered && option.subtitle) {
                        <span class="dept__chip-number">{{ option.subtitle }}</span>
                      }
                    </span>
                  </button>
                } @empty {
                  <div class="sugg sugg--message" role="presentation">No offices found</div>
                }
              </div>
            }
          </div>
        </div>
      }

      <div class="dept__name">
        <span class="m-label">Name</span>
        @if (departmentForm.controls.useCustomName.value) {
          @if (departmentForm.controls.customName.value.trim()) {
            <span class="dept__name-value">{{ departmentForm.controls.customName.value }}</span>
          } @else {
            <span class="dept__name-value dept__name-value--muted">Enter a custom name...</span>
          }
        } @else if (builtName) {
          <span class="dept__name-value" [class.dept__name-value--dup]="isDuplicateName">{{ builtName }}</span>
        } @else {
          <span class="dept__name-value dept__name-value--muted">Select the parts above...</span>
        }
      </div>

      @if (isDuplicateName) {
        <p class="dept__error">A contact with this name already exists.</p>
      }

      <label class="dept__check">
        <input type="checkbox" formControlName="useCustomName" />
        <span>Use a custom name instead</span>
      </label>

      @if (departmentForm.controls.useCustomName.value) {
        <input
          class="m-input"
          type="text"
          formControlName="customName"
          placeholder="e.g. Air Export Sydney"
          aria-label="Custom department name" />
        <p class="dept__warn">
          <mat-icon>warning</mat-icon>
          <span>Custom names sit outside the convention — only use one where the three parts don't fit.</span>
        </p>
        @if (showNameRequiredError) {
          <p class="dept__error">Enter a name</p>
        }
        @if (showOfficeRequiredError) {
          <p class="dept__error">Office location is required</p>
        }
      } @else {
        @if (showOfficeRequiredError) {
          <p class="dept__error">Office location is required</p>
        }
        @if (showProductOrFunctionRequiredError) {
          <p class="dept__error">Select a product and/or a function</p>
        }
      }
    </fieldset>

    <div class="dept__row">
      <div class="dept__field">
        <label class="m-label" for="dept-email">Email</label>
        <input id="dept-email" class="m-input" type="email" formControlName="email" />
        @if (emailError) {
          <p class="dept__error">{{ emailError }}</p>
        } @else if (showContactMethodError) {
          <p class="dept__error">Enter an email, business phone or mobile phone</p>
        }
      </div>
      <div class="dept__field">
        <label class="m-label" for="dept-phone">Business phone</label>
        <app-phone-input id="dept-phone" formControlName="phone" />
        @if (phoneError) {
          <p class="dept__error">{{ phoneError }}</p>
        }
      </div>
    </div>

    <!-- Grid, not a standalone .dept__field: leaves this at the same half-row width as Email/
         Business phone above instead of stretching across the whole dialog. -->
    <div class="dept__row">
      <div class="dept__field">
        <label class="m-label" for="dept-mobile">Mobile phone</label>
        <app-phone-input id="dept-mobile" formControlName="mobilePhone" />
        @if (mobilePhoneError) {
          <p class="dept__error">{{ mobilePhoneError }}</p>
        }
        <p class="dept__hint">Email, business phone or mobile phone — at least one is required.</p>
      </div>
    </div>

    <div class="dept__field">
      <label class="m-label" for="dept-description">Description</label>
      <input
        id="dept-description"
        class="m-input"
        type="text"
        formControlName="description"
        maxlength="500"
        placeholder="e.g. Your team that will support all your Import Air jobs into Sydney." />
      @if (descriptionError) {
        <p class="dept__error">{{ descriptionError }}</p>
      }
    </div>

    <!-- Read-only on purpose: the address is whatever the selected branch office carries, so it
         is never edited here and never sent as anything other than that office's address. -->
    <div class="dept__field">
      <label class="m-label" for="dept-address">Address</label>
      <input
        id="dept-address"
        class="m-input"
        type="text"
        disabled
        placeholder="Select an office location above"
        [value]="address" />
      <p class="dept__hint">Set by the selected office location.</p>
    </div>

    <label class="dept__check dept__active">
      <input type="checkbox" formControlName="isActive" />
      <span class="dept__active-label">Active</span>
      <span class="dept__hint">Inactive contacts are hidden from customers.</span>
    </label>

    <section class="dept__assign">
      <div class="dept__assign-head">
        <span class="m-label">Customer assignment</span>
        <span class="dept__count">{{ assignments.length }} assigned</span>
      </div>

      @if (assignments.length) {
        <div class="dept__chips">
          @for (assignment of assignments; track assignment.id) {
            <div class="dept__chip">
              <mat-icon class="dept__chip-icon material-symbols-outlined">domain</mat-icon>
              <span class="dept__chip-text">
                <span class="dept__chip-name">{{ assignment.name }}</span>
                <span class="dept__chip-number">{{ assignment.number }}</span>
              </span>
              <button
                type="button"
                class="chip-x"
                [attr.aria-label]="'Remove ' + assignment.name"
                (click)="removeAssignment(assignment.id)">
                &times;
              </button>
            </div>
          }
        </div>
        <button type="button" class="dept__clear" (click)="clearAssignments()">Clear all</button>
      } @else {
        <p class="dept__hint">No parties assigned yet — search below to add.</p>
      }

      <div class="dept__search">
        <mat-icon class="dept__search-icon">search</mat-icon>
        <input
          class="dept__search-input"
          type="text"
          role="combobox"
          autocomplete="off"
          aria-autocomplete="list"
          aria-controls="dept-party-list"
          [attr.aria-activedescendant]="partyHighlight >= 0 ? 'dept-party-opt-' + partyHighlight : null"
          placeholder="Search parties by name or customer number..."
          aria-label="Search parties by name or customer number"
          [formControl]="partyControl"
          (keydown)="onPartyKeydown($event)" />
      </div>

      @if (partySearchState$ | async; as state) {
        @if (state.loading || state.error || state.searched) {
          <div class="sugg-panel" id="dept-party-list" role="listbox" aria-label="Parties">
            @if (state.loading) {
              <div class="sugg sugg--message" role="presentation">
                <mat-spinner diameter="16" /><span>Searching...</span>
              </div>
            } @else if (state.error) {
              <div class="sugg sugg--message" role="presentation">Unable to load parties</div>
            } @else {
              @for (party of state.parties; track party.id; let i = $index) {
                <button
                  type="button"
                  role="option"
                  [id]="'dept-party-opt-' + i"
                  class="sugg"
                  [class.sugg--active]="i === partyHighlight"
                  [attr.aria-selected]="i === partyHighlight"
                  (click)="addAssignment(party)">
                  <span class="dept__chip-name">{{ party.name }}</span>
                  <span class="dept__chip-number">{{ party.number }}</span>
                </button>
              } @empty {
                <div class="sugg sugg--message" role="presentation">No parties found</div>
              }
            }
          </div>
        }
      }
    </section>
  </form>
</mat-dialog-content>

<mat-dialog-actions class="dept__actions">
  <button type="button" class="m-btn m-btn-secondary" mat-dialog-close>Cancel</button>
  <button type="button" class="m-btn m-btn-primary" [disabled]="saving || !isFormValid" (click)="save()">
    {{ saving ? (isEditMode ? 'Saving...' : 'Creating...') : isEditMode ? 'Save changes' : 'Create contact' }}
    <span class="sgl-arrows" aria-hidden="true"></span>
  </button>
</mat-dialog-actions>
